// shared-components/services/AuthService.ts

import { supabase } from "@shared/core/services/SupabaseService";
import type {
  AuthResponse,
  Session,
  AuthChangeEvent,
  SupabaseClient,
} from "@supabase/supabase-js";

// Currently unused but may be needed for future role-based access features
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface UserWebsiteAccess {
  id: string;
  user_id: string;
  website_id: string;
  role: string;
  created_at: string;
  updated_at: string;
}

// MFA Factor interface for TOTP authentication
export interface MFAFactor {
  id: string;
  friendly_name: string;
  factor_type: "totp";
  status: "verified" | "unverified";
  created_at: string;
  updated_at: string;
}

// MFA enrollment response interface
export interface MFAEnrollmentResponse {
  qrCodeUri: string;
  secret: string;
  factorId: string;
}

class AuthService {
  private supabase: SupabaseClient;

  constructor() {
    this.supabase = supabase; // Use the shared instance
  }

  async login(
    email: string,
    password: string,
    _rememberMe: boolean = false,
  ): Promise<AuthResponse> {
    const { data, error } = await this.supabase.auth.signInWithPassword({
      email,
      password,
      // persistSession removed in Supabase v2
      // Sessions are now always persisted
      // The _rememberMe parameter is kept for backward compatibility
    });

    if (error) {
      throw new Error(error.message);
    }

    return { data, error };
  }

  async signUp(
    email: string,
    password: string,
    websiteId?: string,
    siteId?: string,
    productionDomain?: string,
  ): Promise<AuthResponse> {
    const metadata: Record<string, string> = {};

    // Add site origin tracking for auth redirect functionality
    // ALWAYS override any existing site metadata with current registration site
    if (websiteId) {
      metadata.website_id = websiteId;
    }
    if (siteId) {
      metadata.site = siteId;
      // Force current site to override any previous registrations
      metadata.current_site = siteId;
    }

    // Construct the verification redirect URL - use production domain if available
    const baseUrl = productionDomain
      ? `https://${productionDomain}`
      : window.location.origin;
    const emailRedirectTo = `${baseUrl}/auth/callback${siteId ? `?site=${siteId}` : ""}`;

    const { data, error } = await this.supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo,
      },
    });

    if (error) {
      throw new Error(error.message);
    }

    return { data, error };
  }

  async verifyWebsiteAccess(
    userId: string,
    websiteId: string,
  ): Promise<boolean> {
    //console.log('Checking access for:', { userId, websiteId });

    if (!userId || !websiteId) {
      //console.warn('Missing userId or websiteId for access check');
      return false;
    }

    const { data, error } = await this.supabase
      .from("user_website_access")
      .select("*")
      .eq("user_id", userId)
      .eq("website_id", websiteId)
      .maybeSingle();

    if (error) {
      //console.error('Error checking website access:', error);
      return false;
    }

    //console.log('Access check result:', data);
    return !!data;
  }

  async resendVerificationEmail(
    email: string,
    emailRedirectTo?: string,
  ): Promise<{ error?: Error }> {
    try {
      const resendOptions: {
        type: "signup";
        email: string;
        options?: { emailRedirectTo: string };
      } = {
        type: "signup",
        email,
      };

      if (emailRedirectTo) {
        resendOptions.options = { emailRedirectTo };
      }

      const { error } = await this.supabase.auth.resend(resendOptions);

      if (error) {
        throw new Error(error.message);
      }

      return { error: undefined };
    } catch (error) {
      return {
        error:
          error instanceof Error
            ? error
            : new Error("Failed to resend verification email"),
      };
    }
  }

  async checkAccountStatus(email: string): Promise<{
    exists: boolean;
    isVerified: boolean;
    needsVerification: boolean;
    error?: Error;
  }> {
    try {
      // Use the resend method to check account status
      // This is safer than attempting to create dummy accounts
      const { error } = await this.supabase.auth.resend({
        type: "signup",
        email,
        options: {
          emailRedirectTo: undefined, // Prevent actual email sending if possible
        },
      });

      if (error) {
        const errorMessage = error.message.toLowerCase();

        if (
          errorMessage.includes("already confirmed") ||
          errorMessage.includes("already verified") ||
          errorMessage.includes("email is already confirmed")
        ) {
          return {
            exists: true,
            isVerified: true,
            needsVerification: false,
            error: undefined,
          };
        }

        if (
          errorMessage.includes("not found") ||
          errorMessage.includes("user not found") ||
          errorMessage.includes("does not exist")
        ) {
          return {
            exists: false,
            isVerified: false,
            needsVerification: false,
            error: undefined,
          };
        }

        if (
          errorMessage.includes("confirmation") ||
          errorMessage.includes("verification") ||
          errorMessage.includes("unconfirmed")
        ) {
          return {
            exists: true,
            isVerified: false,
            needsVerification: true,
            error: undefined,
          };
        }

        // Other errors (rate limiting, etc.)
        return {
          exists: false,
          isVerified: false,
          needsVerification: false,
          error: new Error(error.message),
        };
      }

      // If no error, the resend was successful, which means account exists but is unverified
      return {
        exists: true,
        isVerified: false,
        needsVerification: true,
        error: undefined,
      };
    } catch (error) {
      return {
        exists: false,
        isVerified: false,
        needsVerification: false,
        error:
          error instanceof Error
            ? error
            : new Error("Account status check failed"),
      };
    }
  }

  async logout(): Promise<void> {
    const { error } = await this.supabase.auth.signOut();
    if (error) {
      throw new Error(error.message);
    }
  }

  async getCurrentSession(): Promise<Session | null> {
    const {
      data: { session },
      error,
    } = await this.supabase.auth.getSession();
    if (error) {
      throw new Error(error.message);
    }
    return session;
  }

  async getAccessToken(): Promise<string | null> {
    const session = await this.getCurrentSession();
    return session?.access_token || null;
  }

  onAuthStateChange(
    callback: (event: AuthChangeEvent, session: Session | null) => void,
  ) {
    return this.supabase.auth.onAuthStateChange(callback);
  }

  // ============================================================================
  // MFA (Multi-Factor Authentication) Methods
  // ============================================================================

  /**
   * Start MFA enrollment - generates QR code for authenticator app
   * @returns Object containing QR code URI, secret, and factor ID
   */
  async enrollMFA(): Promise<MFAEnrollmentResponse> {
    const { data, error } = await this.supabase.auth.mfa.enroll({
      factorType: "totp",
      friendlyName: "Authenticator App",
    });

    if (error) {
      throw new Error(
        `MFA enrollment failed: ${error.message || "Unknown error"}`,
      );
    }

    if (!data || !data.totp) {
      throw new Error("MFA enrollment returned no data");
    }

    // Use the plain URI (otpauth://) instead of the pre-rendered SVG data URI
    // This allows the QR code component to properly encode it
    return {
      qrCodeUri: data.totp.uri || data.totp.qr_code,
      secret: data.totp.secret,
      factorId: data.id,
    };
  }

  /**
   * Verify MFA enrollment with 6-digit code from authenticator app
   * @param factorId - The factor ID from enrollMFA
   * @param code - 6-digit verification code
   */
  async verifyMFAEnrollment(factorId: string, code: string): Promise<void> {
    // During enrollment, we need to create a challenge first, then verify it
    const { data: challengeData, error: challengeError } =
      await this.supabase.auth.mfa.challenge({
        factorId,
      });

    if (challengeError) {
      throw new Error(
        `Failed to create MFA challenge: ${challengeError.message}`,
      );
    }

    if (!challengeData) {
      throw new Error("MFA challenge returned no data");
    }

    // Now verify the challenge with the TOTP code
    const { error: verifyError } = await this.supabase.auth.mfa.verify({
      factorId,
      challengeId: challengeData.id,
      code,
    });

    if (verifyError) {
      throw new Error(verifyError.message);
    }
  }

  /**
   * Challenge MFA during login - verify code and complete authentication
   * @param factorId - The factor ID to challenge
   * @param code - 6-digit verification code
   */
  async challengeMFA(factorId: string, code: string): Promise<AuthResponse> {
    // During login, create a challenge first
    const { data: challengeData, error: challengeError } =
      await this.supabase.auth.mfa.challenge({
        factorId,
      });

    if (challengeError) {
      console.error("MFA Login Challenge Error:", challengeError);
      throw new Error(
        `Failed to create MFA challenge: ${challengeError.message}`,
      );
    }

    if (!challengeData) {
      throw new Error("MFA challenge returned no data");
    }

    // Now verify the challenge with the TOTP code
    const { data, error } = await this.supabase.auth.mfa.verify({
      factorId,
      challengeId: challengeData.id,
      code,
    });

    if (error) {
      throw new Error(error.message);
    }

    return { data, error };
  }

  /**
   * Unenroll (disable) MFA for the current user
   * @param factorId - The factor ID to unenroll
   */
  async unenrollMFA(factorId: string): Promise<void> {
    const { error } = await this.supabase.auth.mfa.unenroll({
      factorId,
    });

    if (error) throw new Error(error.message);
  }

  /**
   * List all active MFA factors for the current user
   * @returns Array of MFA factors
   */
  async listMFAFactors(): Promise<MFAFactor[]> {
    const { data, error } = await this.supabase.auth.mfa.listFactors();

    if (error) throw new Error(error.message);

    return data.totp || [];
  }

  /**
   * Generate recovery codes for MFA
   * These are single-use backup codes in case the authenticator is unavailable
   * @returns Array of 10 recovery codes
   */
  async generateRecoveryCodes(): Promise<string[]> {
    // Generate 10 random recovery codes
    const codes = Array.from({ length: 10 }, () =>
      Math.random().toString(36).substring(2, 10).toUpperCase(),
    );

    // Store recovery codes in user metadata
    const { error } = await this.supabase.auth.updateUser({
      data: { recovery_codes: codes },
    });

    if (error) {
      throw new Error(`Failed to store recovery codes: ${error.message}`);
    }

    return codes;
  }

  /**
   * Verify and use a recovery code to complete MFA login
   * Recovery codes are single-use and removed after verification
   * Uses server-side RPC function to bypass AAL1 limitation
   * @param code - The recovery code entered by user
   * @param factorId - The MFA factor ID to temporarily bypass
   * @returns AuthResponse with session data
   */
  async verifyRecoveryCode(
    code: string,
    factorId: string,
  ): Promise<AuthResponse> {
    // Get current session (user is partially authenticated at AAL1)
    const session = await this.getCurrentSession();
    if (!session?.user) {
      throw new Error("No active session found");
    }

    // Normalize the recovery code
    const normalizedCode = code.trim().toUpperCase();

    // Call server-side function to verify and disable MFA
    // This uses SECURITY DEFINER to bypass AAL1 restrictions
    const { data: rpcData, error: rpcError } = await this.supabase.rpc(
      "verify_recovery_code_and_disable_mfa",
      {
        user_id_param: session.user.id,
        recovery_code_param: normalizedCode,
        factor_id_param: factorId,
      },
    );

    if (rpcError) {
      throw new Error(`Recovery code verification failed: ${rpcError.message}`);
    }

    if (!rpcData?.success) {
      throw new Error(rpcData?.error || "Invalid recovery code");
    }

    // Refresh session to get a new session without MFA requirement
    const { data, error } = await this.supabase.auth.refreshSession();

    if (error) {
      throw new Error(`Failed to refresh session: ${error.message}`);
    }

    return { data, error };
  }
}

export const authService = new AuthService();
